import { useSelector } from "react-redux";
import _ from "lodash";

const useHealth = () => {
  const { healthData, healthLoading, healthError, lastUpdated, recoveries, recoveriesCount } = useSelector(
    (store) => store.healthReducer
  );

  // Compute derived data
  const isHealthy = healthData?.status === "healthy";
  const hasError = !_.isEmpty(healthError);
  const hasData = !_.isEmpty(healthData);

  // SMPP specific data (matching actual API response)
  const smppService = healthData?.services?.smpp || {};
  const smppState = (smppService.state || "UNKNOWN").toUpperCase();
  const smppIsHealthy = smppService.healthy || false;

  // SMPP metrics from the service object
  const smppUptime = smppService.uptime || 0;
  const smppMessagesSent = smppService.messagesSent || 0;
  const smppMessagesDelivered = smppService.messagesDelivered || 0;
  const smppDeliveryRate = smppService.deliveryRate || "N/A";

  // Database status
  const databaseService = healthData?.services?.database || {};
  const databaseConnected = databaseService.healthy || false;
  const databaseError = databaseService.error;

  // Metrics from the actual API response
  const metricsData = healthData?.metrics || {};
  const operations24h = metricsData.operations_last_24h || 0;
  const operations1h = metricsData.operations_last_1h || 0;

  // 24-hour breakdown
  const breakdown24h = metricsData.breakdown_24h || {};
  const rabbitmqQueueLength24h = breakdown24h.rabbitmq_queue_length || 0;
  const smppBound24h = breakdown24h.smpp_bound || 0;
  const smppConnected24h = breakdown24h.smpp_connected || 0;
  const smppDisconnected24h = breakdown24h.smpp_disconnected || 0;
  const smppUnbound24h = breakdown24h.smpp_unbound || 0;

  // 1-hour breakdown
  const breakdown1h = metricsData.breakdown_1h || {};
  const rabbitmqQueueLength1h = breakdown1h.rabbitmq_queue_length || 0;
  const smppBound1h = breakdown1h.smpp_bound || 0;
  const smppConnected1h = breakdown1h.smpp_connected || 0;
  const smppDisconnected1h = breakdown1h.smpp_disconnected || 0;
  const smppUnbound1h = breakdown1h.smpp_unbound || 0;

  // Uptime formatting (convert seconds to readable format)
  const formatUptime = (seconds) => {
    if (!seconds || seconds === 0) return "N/A";
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ${totalSeconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${totalSeconds % 60}s`;
    return `${totalSeconds}s`;
  };

  const uptimeFormatted = formatUptime(smppUptime);

  return {
    // Raw data
    healthData,
    healthLoading,
    healthError,
    lastUpdated,

    // Computed booleans
    isHealthy,
    hasError,
    hasData,
    databaseConnected,

    // SMPP data
    smppState,
    smppIsHealthy,
    smppUptime,
    smppMessagesSent,
    smppMessagesDelivered,
    smppDeliveryRate,
    uptimeFormatted,

    // Database
    databaseError,

    // Overall metrics
    operations24h,
    operations1h,

    // 24h breakdown
    rabbitmqQueueLength24h,
    smppBound24h,
    smppConnected24h,
    smppDisconnected24h,
    smppUnbound24h,

    // 1h breakdown
    rabbitmqQueueLength1h,
    smppBound1h,
    smppConnected1h,
    smppDisconnected1h,
    smppUnbound1h,

    // Recovery history
    recoveries,
    recoveriesCount,
  };
};

export default useHealth;
